import {
  Avatar,
  AvatarTypeMap,
  Badge,
  BadgeTypeMap,
  Button,
  Link,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  ListItemTypeMap,
  SxProps,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import * as React from 'react';
import { SyntheticEvent } from 'react';

import { TooltippedTruncatedText } from '~/components/TooltippedTruncatedText';
import { mergeSxProps } from '~/utils/helpers/sxProps';

import { useSxStyles } from './useStyles';

interface CustomSxStyles {
  listItemRoot?: SxProps<Theme>;
  listItemTextPrimary?: SxProps<Theme>;
}

export interface Props {
  avatarProps?: AvatarTypeMap['props'];
  badgeProps?: BadgeTypeMap['props'];
  listItemProps?: ListItemTypeMap<{}, 'div'>['props'];
  href?: string;
  primaryElement: string | JSX.Element;
  secondaryElement?: string | JSX.Element;
  isActive?: boolean;
  onClick?: (event: SyntheticEvent) => void;
  customSxStyles?: CustomSxStyles;
  title?: string;
}

const SummaryItemContent = (itemProps: Props) => {
  const {
    avatarProps,
    title,
    badgeProps,
    primaryElement,
    secondaryElement,
    customSxStyles,
  } = itemProps;

  const sxStyles = useSxStyles();

  const customSx = {
    avatar: mergeSxProps(sxStyles.avatar, avatarProps?.sx),
    listItemTextPrimary: mergeSxProps(
      sxStyles.listItemTextPrimary,
      customSxStyles?.listItemTextPrimary,
    ),
    listItemTextSecondary: mergeSxProps(sxStyles.listItemTextSecondary),
  };

  return (
    <>
      {!!avatarProps && (
        <ListItemAvatar title={title}>
          {!!badgeProps ? (
            <Badge
              {...badgeProps}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              sx={sxStyles.badge}
            >
              <Avatar
                {...avatarProps}
                className={'avatar'}
                data-cy="profile-pic"
                sx={customSx.avatar}
              />
            </Badge>
          ) : (
            <Avatar
              {...avatarProps}
              className={'avatar'}
              data-cy="profile-pic"
              sx={customSx.avatar}
            />
          )}
        </ListItemAvatar>
      )}
      <ListItemText
        sx={sxStyles.listItemTextMultiline}
        primary={
          <TooltippedTruncatedText>{primaryElement}</TooltippedTruncatedText>
        }
        secondary={
          secondaryElement && (
            <TooltippedTruncatedText>
              {secondaryElement}
            </TooltippedTruncatedText>
          )
        }
        slotProps={{
          primary: {
            component: 'div',
            sx: customSx.listItemTextPrimary,
          },
          secondary: {
            component: 'div',
            sx: customSx.listItemTextSecondary,
          },
        }}
      />
    </>
  );
};

export const SummaryItem: React.FC<Props> = (props) => {
  const { href, listItemProps, onClick, isActive, customSxStyles } = props;
  const sxStyles = useSxStyles();

  const customSx = {
    listItemRoot: mergeSxProps(
      sxStyles.listItemRoot,
      customSxStyles?.listItemRoot,
    ),
  };

  const commonProps = {
    'data-cy': 'list-item',
    'data-sf': 'SummaryItem',
  };

  const commonButtonProps = {
    ...commonProps,
    selected: isActive,
    'data-selected': isActive,
  };

  if (!!href) {
    return (
      <ListItem sx={{ padding: 0 }}>
        <ListItemButton
          sx={customSx.listItemRoot}
          href={href || ''}
          component={Link}
          {...listItemProps}
          {...commonButtonProps}
        >
          <SummaryItemContent {...props} />
        </ListItemButton>
      </ListItem>
    );
  } else if (!!onClick) {
    return (
      <ListItem sx={{ padding: 0 }}>
        <ListItemButton
          sx={customSx.listItemRoot}
          onMouseDown={onClick}
          component={Button}
          {...listItemProps}
          {...commonButtonProps}
        >
          <SummaryItemContent {...props} />
        </ListItemButton>
      </ListItem>
    );
  } else {
    return (
      <ListItem sx={customSx.listItemRoot} {...commonProps}>
        <SummaryItemContent {...props} />
      </ListItem>
    );
  }
};
